import React, { Component } from 'react';
import { Checkbox, Form, Button, Card, message, Input, Switch, Divider, Tooltip, DatePicker, Table, Spin, Modal, Tabs } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import compose from 'lodash/fp/compose';
import moment from 'moment';

import './index.scss';

import API from '../../../api';
import { promiseLoader, refreshAll } from '../../../components/common/promise-loader';
import { connect } from 'react-redux';
import { number, object } from 'prop-types';

const { TabPane } = Tabs;

export class StaffMailerIndex extends Component {
    static propTypes = {
      company: object,
      companyId: number
    };

    state = {
      switchEvents: this.props.company.setting.dailyMail,
      scheduledMails: [],
      scheduledDate: moment(),
      loadingScheduledMails: false,
      sentMails: [],
      loadingSentMails: false,
      activeTab: 'scheduled',
      previewVisible: false,
      selectedPreview: null
    };

    componentDidMount () {
      this.loadDataForTab(this.state.activeTab, this.state.scheduledDate);
    }

    componentDidUpdate (prevProps, prevState) {
      if (prevProps.company.setting.dailyMail !== this.props.company.setting.dailyMail) {
        this.setState({ switchEvents: this.props.company.setting.dailyMail });
      }

      if (
        prevState.scheduledDate &&
        this.state.scheduledDate &&
        !prevState.scheduledDate.isSame(this.state.scheduledDate, 'day')
      ) {
        this.loadDataForTab(this.state.activeTab, this.state.scheduledDate);
      }
    }

    onChangeSwitch = () => {
      this.setState((prev) => ({
        switchEvents: !prev.switchEvents
      }));
    };

    handleSubmit = (attributes) => {
      const { companyId } = this.props;
      const checked = attributes.checked;
      const updateMailDays = attributes.updateMailDays;
      const settingAttributes = {
        ...attributes,
        addCommentMail: this.state.switchEvents ? false : checked.includes('¿Enviar mail de notificación de comentario agregado?'),
        addAttachmentMail: this.state.switchEvents ? false : checked.includes('¿Enviar mail de notificación de documento agregado?'),
        startStageMail: this.state.switchEvents ? false : checked.includes('¿Enviar mail de notificación de inicio de etapa?'),
        dailyMail: this.state.switchEvents,
        updateMailDays: this.state.switchEvents ? updateMailDays : []
      };
      API.staff.updateCompany(companyId, { settingAttributes }).then(() => {
        refreshAll();
        message.success('Configuración actualizada');
      }).catch((e) => {
        message.error('Error');
      });
    };

    handleDateChange = (date) => {
      this.setState({ scheduledDate: date || moment() });
    };

    loadDataForTab = (tab, date) => {
      if (tab === 'sent') {
        this.loadSentMails(date);
      } else {
        this.loadScheduledMails(date);
      }
    };

    loadScheduledMails = (date) => {
      const { companyId } = this.props;
      if (!companyId) return;

      const formattedDate = date ? date.format('YYYY-MM-DD') : undefined;

      this.setState({ loadingScheduledMails: true });
      API.staff.getCompanyScheduledMails(companyId, formattedDate)
        .then((scheduledMails) => {
          this.setState({ scheduledMails, previewVisible: false, selectedPreview: null });
        })
        .catch(() => {
          message.error('No se pudieron cargar los correos programados');
        })
        .finally(() => {
          this.setState({ loadingScheduledMails: false });
        });
    };

    loadSentMails = (date) => {
      const { companyId } = this.props;
      if (!companyId) return;

      const formattedDate = date ? date.format('YYYY-MM-DD') : undefined;

      this.setState({ loadingSentMails: true });
      API.staff.getCompanySentMails(companyId, formattedDate)
        .then((sentMails) => {
          this.setState({ sentMails, previewVisible: false, selectedPreview: null });
        })
        .catch(() => {
          message.error('No se pudieron cargar los correos enviados');
        })
        .finally(() => {
          this.setState({ loadingSentMails: false });
        });
    };

    openPreview = (record) => {
      this.setState({
        previewVisible: true,
        selectedPreview: record.preview
      });
    };

    closePreview = () => {
      this.setState({
        previewVisible: false,
        selectedPreview: null
      });
    };

    handleTabChange = (tabKey) => {
      this.setState({ activeTab: tabKey }, () => {
        this.loadDataForTab(tabKey, this.state.scheduledDate);
      });
    };

    renderScheduledMailsTable () {
      const { scheduledMails, loadingScheduledMails, scheduledDate } = this.state;

      const columns = [
        {
          title: 'Proceso',
          dataIndex: 'saleProcessName',
          key: 'saleProcessName'
        },
        {
          title: 'Rol',
          dataIndex: 'recipientRole',
          key: 'recipientRole',
          render: (role) => role === 'buyer' ? 'Comprador' : 'Vendedor'
        },
        {
          title: 'Destinatario',
          dataIndex: 'recipientName',
          key: 'recipientName'
        },
        {
          title: 'Email',
          dataIndex: 'recipientEmail',
          key: 'recipientEmail'
        },
        {
          title: 'CC',
          dataIndex: 'ccEmails',
          key: 'ccEmails',
          render: (ccEmails = []) => ccEmails.join(', ')
        },
        {
          title: 'Preview',
          key: 'preview',
          render: (_text, record) => (
            <Button type='link' htmlType='button' onClick={() => this.openPreview(record)}>
              Ver preview
            </Button>
          )
        }
      ];

      const dataSource = scheduledMails.map((mail) => ({
        key: mail.id,
        ...mail
      }));

      const pagination = {
        position: ['bottomCenter'],
        pageSize: 5,
        showSizeChanger: false
      };

      return (
        <div className='scheduled-mails-section'>
          <div className='scheduled-mails-header'>
            <h3>Correos programados</h3>
            <DatePicker
              allowClear={false}
              value={scheduledDate}
              onChange={this.handleDateChange}
              format='YYYY-MM-DD'
            />
          </div>
          <Spin spinning={loadingScheduledMails}>
            <Table
              locale={{ emptyText: 'Sin correos programados para esta fecha' }}
              dataSource={dataSource}
              columns={columns}
              pagination={pagination}
              rowKey='id'
              size='small'
            />
          </Spin>
        </div>
      );
    }

    renderSentMailsTable () {
      const { sentMails, loadingSentMails, scheduledDate } = this.state;

      const columns = [
        {
          title: 'Proceso',
          dataIndex: 'saleProcessName',
          key: 'saleProcessName'
        },
        {
          title: 'Rol',
          dataIndex: 'recipientRole',
          key: 'recipientRole',
          render: (role) => role === 'buyer' ? 'Comprador' : 'Vendedor'
        },
        {
          title: 'Destinatario',
          dataIndex: 'recipientName',
          key: 'recipientName'
        },
        {
          title: 'Email',
          dataIndex: 'recipientEmail',
          key: 'recipientEmail'
        },
        {
          title: 'CC',
          dataIndex: 'ccEmails',
          key: 'ccEmails',
          render: (ccEmails = []) => ccEmails.join(', ')
        },
        {
          title: 'Enviado',
          dataIndex: 'sentAt',
          key: 'sentAt',
          render: (sentAt) => (sentAt ? moment(sentAt).format('YYYY-MM-DD HH:mm') : '—')
        },
        {
          title: 'Preview',
          key: 'preview',
          render: (_text, record) => (
            <Button type='link' htmlType='button' onClick={() => this.openPreview(record)}>
              Ver preview
            </Button>
          )
        }
      ];

      const pagination = {
        position: ['bottomCenter'],
        pageSize: 5,
        showSizeChanger: false
      };

      const dataSource = sentMails.map((mail) => ({
        key: mail.id,
        ...mail
      }));

      return (
        <div className='scheduled-mails-section'>
          <div className='scheduled-mails-header'>
            <h3>Correos enviados</h3>
            <DatePicker
              allowClear={false}
              value={scheduledDate}
              onChange={this.handleDateChange}
              format='YYYY-MM-DD'
            />
          </div>
          <Spin spinning={loadingSentMails}>
            <Table
              locale={{ emptyText: 'Sin correos enviados para esta fecha' }}
              dataSource={dataSource}
              columns={columns}
              pagination={pagination}
              rowKey='id'
              size='small'
            />
          </Spin>
        </div>
      );
    }

    renderPreviewModal () {
      const { previewVisible, selectedPreview } = this.state;

      return (
        <Modal
          visible={previewVisible}
          onCancel={this.closePreview}
          footer={null}
          title={selectedPreview ? selectedPreview.subject : ''}
        >
          {selectedPreview && (
            selectedPreview.html
              ? (
                <div
                  className='mail-preview'
                  dangerouslySetInnerHTML={{ __html: selectedPreview.html }}
                />
              )
              : (() => {
                const stats = selectedPreview.stats || {};
                const stageUpdates = stats.stageUpdates || stats.stage_updates || [];
                const commentsCount = stats.commentsCount ?? stats.comments_count ?? 0;
                const attachmentsCount = stats.attachmentsCount ?? stats.attachments_count ?? 0;
                const ctaPath = selectedPreview.ctaPath || selectedPreview.cta_path;
                const intervalDays = selectedPreview.intervalDays || selectedPreview.interval_days;

                return (
                  <div className='mail-preview'>
                    <p>{selectedPreview.greeting}</p>
                    <p>{selectedPreview.message}</p>
                    <div className='mail-preview__stats'>
                      <p><strong>Comentarios nuevos:</strong> {commentsCount}</p>
                      <p><strong>Documentos nuevos:</strong> {attachmentsCount}</p>
                    </div>
                    <div className='mail-preview__stages'>
                      <strong>Actualizaciones de etapa:</strong>
                      {stageUpdates.length > 0 ? (
                        <ul>
                          {stageUpdates.map((stage, index) => (
                            <li key={`${stage.name}-${index}`}>
                              {stage.name} → {stage.status}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>No hubo cambios en las etapas</p>
                      )}
                    </div>
                    <div className='mail-preview__cta'>
                      <strong>Enlace al detalle:</strong> {ctaPath}
                    </div>
                    {intervalDays !== null && (
                      <p className='mail-preview__interval'>
                        Período considerado: últimos {intervalDays} día(s).
                      </p>
                    )}
                  </div>
                );
              })()
          )}
        </Modal>
      );
    }

    getInitialValues = () => {
      const { company: { setting } } = this.props;
      const checkedOptions = [];
      if (setting) {
        if (setting.addCommentMail) {
          checkedOptions.push('¿Enviar mail de notificación de comentario agregado?');
        }
        if (setting.addAttachmentMail) {
          checkedOptions.push('¿Enviar mail de notificación de documento agregado?');
        }
        if (setting.startStageMail) {
          checkedOptions.push('¿Enviar mail de notificación de inicio de etapa?');
        }
      }
      return {
        checked: checkedOptions,
        ccoMail: setting.ccoMail,
        updateMailDays: setting.updateMailDays
      };
    };

    render () {
      const options = [
        '¿Enviar mail de notificación de inicio de etapa?',
        '¿Enviar mail de notificación de comentario agregado?',
        '¿Enviar mail de notificación de documento agregado?'
      ];

      const days = [
        { label: 'Lunes', value: 1 },
        { label: 'Martes', value: 2 },
        { label: 'Miércoles', value: 3 },
        { label: 'Jueves', value: 4 },
        { label: 'Viernes', value: 5 },
        { label: 'Sábado', value: 6 },
        { label: 'Domingo', value: 0 }
      ];

      const inactiveColor = 'rgba(0, 0, 0, 0.25)';

      const activeColor = 'rgba(0, 0, 0, 0.65)';

      return (
        <div className='mailer-edit--container'>
          <Card className='text-left p-3 mailer-edit--settings'>
            <h2 className='text-center mt-3'>Configuración de correos</h2>
            <div className='grid-container switch-container'>
              <div>
                <Tooltip title='Elija evento por el cual desea recibir novedades sobre su proceso de postventa' color={this.state.switchEvents ? activeColor : '#73b680'}>
                  <QuestionCircleOutlined style={{ color: this.state.switchEvents ? inactiveColor : '#73b680' }}/>
                </Tooltip>
                <strong style={{ marginLeft: '10px', color: this.state.switchEvents ? inactiveColor : activeColor }}>
                  Seleccione por Evento
                </strong>
              </div>
              <div>
                <Switch onChange={this.onChangeSwitch} defaultChecked={this.state.switchEvents} style={{ backgroundColor: '#73b680' }}/>
              </div>
              <div>
                <Tooltip title='Elija los días de semana que desea recibir novedades sobre su proceso de postventa' color={!this.state.switchEvents ? activeColor : '#73b680'}>
                  <QuestionCircleOutlined style={{ color: !this.state.switchEvents ? inactiveColor : '#73b680' }}/>
                </Tooltip>
                <strong style={{ marginLeft: '10px', color: !this.state.switchEvents ? inactiveColor : activeColor }}>
                  Seleccione días para mail de novedades
                </strong>
              </div>
            </div>
            <br />
            <Form name='email-setings' onFinish={this.handleSubmit} initialValues={this.getInitialValues()}>
              <div className="grid-container checkbox-container">
                <Form.Item name='checked'>
                  <Checkbox.Group options={options} disabled={this.state.switchEvents} />
                </Form.Item>
                <div className='checkbox-groups-divider'>
                  <Divider type='vertical' style={{ height: '200px', backgroundColor: inactiveColor }}/>
                </div>
                <Form.Item name='updateMailDays'>
                  <Checkbox.Group className="update-mail-days" options={days} disabled={!this.state.switchEvents}/>
                </Form.Item>
              </div>
              <br />
              <Form.Item name='ccoMail' label={
                <div>
                  <Tooltip title='Enviar copia oculta a otros destinatarios' color={'#73b680'}>
                    <QuestionCircleOutlined style={{ color: '#73b680' }} />
                  </Tooltip>
                  <span style={{ marginLeft: '10px' }}>CCO Email</span>
                </div>
              }>
                <Input/>
              </Form.Item>
              <Form.Item className='text-right'>
                <Button type="primary" htmlType="submit">
                  Confirmar
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card className='text-left p-3 mailer-edit--logs'>
            <h2 className='text-center mt-3'>Gestión de envíos</h2>
            <Tabs
              className='mailer-tabs'
              activeKey={this.state.activeTab}
              onChange={this.handleTabChange}
            >
              <TabPane tab='Correos programados' key='scheduled'>
                {this.renderScheduledMailsTable()}
              </TabPane>
              <TabPane tab='Correos enviados' key='sent'>
                {this.renderSentMailsTable()}
              </TabPane>
            </Tabs>
          </Card>

          {this.renderPreviewModal()}
        </div>
      );
    }
}

const mapStateToProps = (state) => ({
  companyId: state.session.currentUser && state.session.currentUser.companyId
});

export default compose(
  connect(mapStateToProps),
  promiseLoader(
    ({ companyId }) => {
      if (companyId) {
        return API.staff.getCompany(companyId);
      } else {
        return null;
      }
    },
    'company', true
  )
)(StaffMailerIndex);
